import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { Pool } from '../../types';
import {
  exportDetectionLabelsYolo,
  fetchDetectionLabelStats,
  fetchDetectionLabelTrainingStatus,
  startDetectionLabelTraining,
  type LabelStats,
  type TrainJobStatus,
} from '../../services/detectionLabelTrainingService';
import DetectionLabelBrowser from './DetectionLabelBrowser';

type Props = {
  pools: Pool[];
  /** 시스템 관리 「기본 weights」에 설정된 파일명 */
  defaultYoloModel?: string | null;
};

function basenameModel(raw: string | null | undefined): string {
  const s = String(raw || '')
    .trim()
    .replace(/\\/g, '/');
  if (!s) return '';
  const parts = s.split('/');
  return parts[parts.length - 1] || s;
}

function poolNumericIds(pools: Pool[]): number[] {
  return pools
    .map((p) => Number(p.id))
    .filter((n) => Number.isFinite(n) && n > 0);
}

/**
 * 일상 관제·탐지 튜닝과 분리된 R&D 기능. 최고관리자만 시스템 관리에 표시됩니다.
 * 라벨은 시설별로 쌓이고, 학습 결과는 운영 기본 .pt 하나(공통)로 합쳐짐.
 */
const DetectionLabelTrainingCard: React.FC<Props> = ({
  pools,
  defaultYoloModel,
}) => {
  const opsModel = useMemo(() => {
    const name = basenameModel(defaultYoloModel);
    return name || 'detector_ver5.pt';
  }, [defaultYoloModel]);

  const allPoolIds = useMemo(() => poolNumericIds(pools), [pools]);
  const [selectedPoolIds, setSelectedPoolIds] = useState<number[]>(() =>
    poolNumericIds(pools),
  );
  const [stats, setStats] = useState<LabelStats | null>(null);
  const [trainStatus, setTrainStatus] = useState<TrainJobStatus | null>(null);
  const [epochs, setEpochs] = useState(40);
  const [imgsz, setImgsz] = useState(1280);
  const [device, setDevice] = useState('0');
  const [copyToOps, setCopyToOps] = useState(true);
  const [busy, setBusy] = useState<'export' | 'train' | 'stats' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exportInfo, setExportInfo] = useState<string | null>(null);
  const [statsInfo, setStatsInfo] = useState<string | null>(null);

  const allSelected =
    allPoolIds.length > 0 &&
    allPoolIds.every((id) => selectedPoolIds.includes(id));
  const someSelected = selectedPoolIds.length > 0;

  const formatStatsMessage = (s: LabelStats, nPools: number) =>
    `집계 결과 — 시설 ${nPools}곳 · 전체 ${s.total}건 (오검출 ${s.false_positive} · 미검출 ${s.missed_detection}) · 프레임 있음 ${s.with_frame}건`;

  const refreshStats = useCallback(
    async (opts?: { fromButton?: boolean; ids?: number[] }) => {
      const ids = opts?.ids ?? selectedPoolIds;
      if (!ids.length) {
        setStats(null);
        setStatsInfo(null);
        if (opts?.fromButton) setError('시설을 하나 이상 선택');
        return;
      }
      if (opts?.fromButton) setBusy('stats');
      try {
        const s = await fetchDetectionLabelStats(ids);
        setStats(s);
        setStatsInfo(formatStatsMessage(s, ids.length));
        setError(null);
      } catch {
        setStats(null);
        setStatsInfo(null);
        if (opts?.fromButton) {
          setError('라벨 집계에 실패. 시설 선택과 서버 상태를 확인.');
        }
      } finally {
        if (opts?.fromButton) setBusy(null);
      }
    },
    [selectedPoolIds],
  );

  const refreshTrainStatus = useCallback(async () => {
    try {
      const st = await fetchDetectionLabelTrainingStatus();
      setTrainStatus(st);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (allPoolIds.length === 0) return;
    setSelectedPoolIds((prev) => {
      const kept = prev.filter((id) => allPoolIds.includes(id));
      if (kept.length > 0) return kept;
      return allPoolIds;
    });
  }, [allPoolIds]);

  useEffect(() => {
    void refreshStats();
    void refreshTrainStatus();
  }, [refreshStats, refreshTrainStatus]);

  useEffect(() => {
    if (!trainStatus?.running) return undefined;
    const t = window.setInterval(() => {
      void refreshTrainStatus();
    }, 4000);
    return () => window.clearInterval(t);
  }, [trainStatus?.running, refreshTrainStatus]);

  const toggleAll = (checked: boolean) => {
    setSelectedPoolIds(checked ? allPoolIds : []);
  };

  const togglePool = (id: number, checked: boolean) => {
    setSelectedPoolIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id].sort((a, b) => a - b);
      return prev.filter((x) => x !== id);
    });
  };

  const onExport = async () => {
    if (!selectedPoolIds.length) {
      setError('시설을 하나 이상 선택');
      return;
    }
    setBusy('export');
    setError(null);
    setExportInfo(null);
    try {
      const r = await exportDetectionLabelsYolo(selectedPoolIds);
      const n = (r.pool_ids && r.pool_ids.length) || selectedPoolIds.length;
      setExportInfo(
        `YOLO 데이터셋(시설 ${n}곳): train ${r.train_images} / val ${r.val_images} · ${r.dataset_dir}`,
      );
      await refreshStats({ ids: selectedPoolIds });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'export 실패';
      setError(String(msg));
    } finally {
      setBusy(null);
    }
  };

  const onTrain = async () => {
    if (!selectedPoolIds.length) {
      setError('시설을 하나 이상 선택');
      return;
    }
    setBusy('train');
    setError(null);
    try {
      const st = await startDetectionLabelTraining({
        pool_ids: selectedPoolIds,
        epochs,
        imgsz,
        device,
        base_model: opsModel,
        copy_to_ver5: copyToOps,
        copy_to_filename: opsModel,
      });
      setTrainStatus(st);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        '학습 시작 실패';
      setError(String(msg));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Accordion
      disableGutters
      defaultExpanded
      sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            모델 고도화 (선택 · R&D)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            시설별 라벨 → 공통 운영 .pt 파인튜닝
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Alert severity="info" sx={{ mb: 2 }}>
          오검출/미검출 라벨은 <strong>시설별로 저장</strong>되고, 학습 결과는 운영 기본
          weights(<strong>{opsModel}</strong>) <strong>하나</strong>에 합쳐집니다. 여러
          시설을 선택하면 라벨을 모아 같은 .pt를 파인튜닝합니다.
        </Alert>
        <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
          현재 시스템 관리 기본 weights: <strong>{opsModel}</strong>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
            학습 시작점과 「운영에도 복사」 대상. 임계 설정 → AI 검출의 「기본 weights」와
            동일.
          </Typography>
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          미검출: swimmer bbox · 오검출: 배경(빈 라벨). 프레임 없는 라벨은 export 제외.
          (P2-15)
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {exportInfo && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {exportInfo}
          </Alert>
        )}
        {statsInfo && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setStatsInfo(null)}>
            {statsInfo}
          </Alert>
        )}
        {trainStatus?.message && (
          <Alert
            severity={
              trainStatus.running
                ? 'info'
                : trainStatus.success
                  ? 'success'
                  : trainStatus.success === false
                    ? 'error'
                    : 'info'
            }
            sx={{ mb: 2 }}
          >
            {trainStatus.running && (
              <CircularProgress size={14} sx={{ mr: 1, verticalAlign: 'middle' }} />
            )}
            {trainStatus.message}
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          학습에 쓸 시설 ({selectedPoolIds.length}/{allPoolIds.length})
        </Typography>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            mb: 2,
            maxHeight: 220,
            overflow: 'auto',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={!allSelected && someSelected}
                onChange={(_, c) => toggleAll(c)}
                disabled={allPoolIds.length === 0}
              />
            }
            label="전체"
          />
          <FormGroup>
            {pools.map((p) => {
              const id = Number(p.id);
              if (!Number.isFinite(id) || id <= 0) return null;
              return (
                <FormControlLabel
                  key={id}
                  control={
                    <Checkbox
                      checked={selectedPoolIds.includes(id)}
                      onChange={(_, c) => togglePool(id, c)}
                    />
                  }
                  label={p.name || p.code || String(id)}
                />
              );
            })}
          </FormGroup>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Button
            startIcon={
              busy === 'stats' ? <CircularProgress size={18} /> : <RefreshIcon />
            }
            disabled={!someSelected || busy !== null || !!trainStatus?.running}
            onClick={() => void refreshStats({ fromButton: true })}
          >
            라벨 집계
          </Button>
        </Box>
        {stats && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            라벨 {stats.total}건 (오검출 {stats.false_positive} · 미검출{' '}
            {stats.missed_detection}) · 프레임 {stats.with_frame}건
            {stats.pool_count != null ? ` · 시설 ${stats.pool_count}곳` : ''}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            type="number"
            label="epochs"
            value={epochs}
            onChange={(e) => setEpochs(Number(e.target.value))}
            inputProps={{ min: 5, max: 300 }}
          />
          <TextField
            size="small"
            type="number"
            label="imgsz"
            value={imgsz}
            onChange={(e) => setImgsz(Number(e.target.value))}
            inputProps={{ min: 320, max: 1920 }}
          />
          <TextField
            size="small"
            label="device"
            value={device}
            onChange={(e) => setDevice(e.target.value)}
            helperText="0 또는 cpu"
          />
          <FormControlLabel
            control={
              <Switch
                checked={copyToOps}
                onChange={(e) => setCopyToOps(e.target.checked)}
              />
            }
            label={`완료 후 운영 기본 weights(${opsModel})에도 복사`}
          />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={busy === 'export' ? <CircularProgress size={18} /> : <DownloadIcon />}
            disabled={!someSelected || busy !== null || !!trainStatus?.running}
            onClick={() => void onExport()}
          >
            YOLO 데이터셋 export
          </Button>
          <Button
            variant="contained"
            startIcon={busy === 'train' ? <CircularProgress size={18} /> : <ModelTrainingIcon />}
            disabled={!someSelected || busy !== null || !!trainStatus?.running}
            onClick={() => void onTrain()}
          >
            export + 학습 시작
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          선택 시설 라벨 합침 → 학습 기준 {opsModel} → 결과 pool_swimmer.pt
          {copyToOps ? ` → 복사 ${opsModel}` : ''} · 적용 시 백엔드 재기동 필요할 수 있음
        </Typography>

        <DetectionLabelBrowser pools={pools} selectedPoolIds={selectedPoolIds} />
      </AccordionDetails>
    </Accordion>
  );
};

export default DetectionLabelTrainingCard;
